// Regular Expressions course — Part 2: Quantifiers, Groups, and Transforming Text.

export const regexPart2 = [
  {
    id: "quantifiers",
    title: "Quantifiers: How Many?",
    group: "Quantifiers",
    summary: "Repeat the previous piece a controlled number of times.",
    content: [
      { t: "p", text: "A quantifier says how many times the thing before it may repeat. The four you'll use most:" },
      { t: "list", items: [
        "* — zero or more",
        "+ — one or more",
        "? — zero or one (optional)",
        "{n} / {n,m} — exactly n, or between n and m",
      ] },
      { t: "p", text: "ab*c means a, then any number of b's (including none), then c." },
      { t: "code", code: "(map #(re-find #\"ab*c\" %) [\"ac\" \"abc\" \"abbbc\"])" },
      { t: "p", text: "Change * to + and 'ac' no longer matches, because + demands at least one b." },
      { t: "code", code: "(map #(re-find #\"ab+c\" %) [\"ac\" \"abc\" \"abbbc\"])" },
      { t: "h", text: "Optional with ?" },
      { t: "p", text: "? makes the previous piece optional. Great for spelling variants like color / colour." },
      { t: "code", code: "(map #(re-find #\"colou?r\" %) [\"color\" \"colour\"])" },
      { t: "h", text: "Exact counts with braces" },
      { t: "p", text: "{3} means exactly three; {2,4} means two to four." },
      { t: "code", code: "(re-find #\"\\d{2,4}\" \"id 1234567\")" },
      { t: "note", text: "A quantifier always applies to the single item immediately before it (a character, a class, or a group)." },
    ],
    exercises: [
      {
        prompt: "Define a pattern (answer) for a US ZIP code: exactly five digits. It should fully match \"90210\" but not \"9021\" or \"902100\".",
        starter: "#\"\"",
        tests: ["(boolean (re-matches answer \"90210\"))", "(nil? (re-matches answer \"9021\"))", "(nil? (re-matches answer \"902100\"))"],
        solution: "#\"\\d{5}\"",
        hint: "Exactly five digits is \\d{5}.",
      },
      {
        prompt: "Braces can express a RANGE. Define a pattern (answer) that matches 2 to 4 digits — two, three or four. It should match \"12\" and \"1234\", but not \"1\" or \"12345\".",
        starter: "#\"\"",
        tests: ["(boolean (re-matches answer \"12\"))", "(boolean (re-matches answer \"1234\"))", "(nil? (re-matches answer \"1\"))", "(nil? (re-matches answer \"12345\"))"],
        solution: "#\"\\d{2,4}\"",
        hint: "{2,4} means two to four: #\"\\d{2,4}\".",
      },
    ],
  },

  {
    id: "greedy-lazy",
    title: "Greedy vs. Lazy",
    group: "Quantifiers",
    summary: "Why a pattern can grab more than you expect.",
    content: [
      { t: "p", text: "By default quantifiers are greedy: they match as MUCH as possible. Consider matching a tag with <.+> against \"<a><b>\". The .+ greedily swallows everything up to the LAST >." },
      { t: "code", code: "(re-find #\"<.+>\" \"<a><b>\")" },
      { t: "p", text: "Add a ? after the quantifier to make it lazy — matching as LITTLE as possible. Now it stops at the first >." },
      { t: "code", code: "(re-find #\"<.+?>\" \"<a><b>\")" },
      { t: "p", text: "See the difference across all matches with re-seq:" },
      { t: "code", code: "(re-seq #\"<.+?>\" \"<a><b><c>\")" },
      { t: "note", text: "Greedy .+ / .* is the #1 cause of 'my regex matched too much'. Reach for the lazy .+? / .*? when you want the smallest match." },
    ],
    exercises: [
      {
        prompt: "Using a LAZY quantifier, extract just the first quoted word from '\"one\" and \"two\"'. Expected result: \"\\\"one\\\"\" (the text including the quotes).",
        starter: "(re-find #\"\" \"\\\"one\\\" and \\\"two\\\"\")",
        tests: ["(= answer \"\\\"one\\\"\")"],
        solution: "(re-find #\"\\\".+?\\\"\" \"\\\"one\\\" and \\\"two\\\"\")",
        hint: "Match a quote, then .+? (lazy), then a quote: #\"\\\".+?\\\"\".",
      },
      {
        prompt: "By default quantifiers are greedy. Use re-find with a GREEDY quantifier to match from the first < to the LAST > in \"<p><b>\". The whole match should be \"<p><b>\".",
        starter: "(re-find #\"\" \"<p><b>\")",
        tests: ["(= answer \"<p><b>\")"],
        solution: "(re-find #\"<.+>\" \"<p><b>\")",
        hint: "Greedy .+ grabs up to the last >: #\"<.+>\".",
      },
    ],
  },

  {
    id: "groups",
    title: "Groups & Capturing",
    group: "Groups & Alternation",
    summary: "Grab pieces out of a match.",
    content: [
      { t: "p", text: "Parentheses ( ) create a group. Groups do two jobs: they let a quantifier apply to several characters at once, and they CAPTURE the matched text so you can pull it out." },
      { t: "p", text: "When a pattern has groups, re-find returns a vector: the whole match first, then each captured group." },
      { t: "code", code: "(re-find #\"(\\d{4})-(\\d{2})-(\\d{2})\" \"date: 2026-06-15\")" },
      { t: "p", text: "Destructure that vector to name the parts. The _ ignores the full match." },
      { t: "code", code: "(let [[_ y m d] (re-find #\"(\\d{4})-(\\d{2})-(\\d{2})\" \"2026-06-15\")]\n  {:year y :month m :day d})" },
      { t: "p", text: "With re-seq, each element is itself a [whole group1 group2 …] vector." },
      { t: "code", code: "(re-seq #\"(\\w)(\\d)\" \"a1 b2 c3\")" },
      { t: "note", text: "Groups quantify AND capture. If you only want grouping (no capture), use a non-capturing group (?:…) — covered next." },
      { t: "h", text: "Backreferences: 'the same text again'" },
      { t: "p", text: "A backreference — \\1, \\2, and so on — matches the SAME text that a group captured earlier. It's the regex way of saying 'whatever that group matched, exactly again'. The classic uses are doubled characters and repeated words." },
      { t: "code", code: "(re-find #\"(\\w+) \\1\" \"say it again again\")" },
      { t: "p", text: "Here \\1 means 'group 1's text', so the pattern is: a word, a space, the same word again. Numbering follows the order of the opening parentheses: \\1 is the first group, \\2 the second, and so on." },
      { t: "p", text: "This also finds doubled letters — the engine behind Regex Golf's first hole:" },
      { t: "code", code: "(re-seq #\"(.)\\1\" \"bookkeeper\")" },
      { t: "p", text: "One subtlety: a backreference repeats the captured TEXT, not the pattern. So (\\d)\\1 matches \"11\" or \"55\", but never \"12\":" },
      { t: "code", code: "(re-find #\"(\\d)\\1\" \"room 1122\")" },
      { t: "note", text: "A group with a backreference is still a group, so re-find and re-seq include it in the result vector — first the whole match, then the captured groups." },
    ],
    exercises: [
      {
        prompt: "Extract just the YEAR from \"2026-06-15\" using a capturing group. Expected: \"2026\".",
        starter: "(second (re-find #\"\" \"2026-06-15\"))",
        tests: ["(= answer \"2026\")"],
        solution: "(second (re-find #\"(\\d{4})-\\d{2}-\\d{2}\" \"2026-06-15\"))",
        hint: "Capture the four year digits: (\\d{4}); second grabs the first group.",
      },
      {
        prompt: "Define a pattern (answer) that matches a word repeated after a space — like \"hi hi\" or \"go go\" — using a backreference. It should NOT match \"hi ho\".",
        starter: "#\"\"",
        tests: ["(boolean (re-find answer \"hi hi\"))", "(boolean (re-find answer \"go go\"))", "(nil? (re-find answer \"hi ho\"))"],
        solution: "#\"(\\w+) \\1\"",
        hint: "Capture a word, then reference it with \\1: (\\w+) \\1.",
      },
    ],
  },

  {
    id: "alternation",
    title: "Alternation & Non-Capturing Groups",
    group: "Groups & Alternation",
    summary: "Match this OR that.",
    content: [
      { t: "p", text: "The pipe | means OR. cat|dog matches either word." },
      { t: "code", code: "(re-seq #\"cat|dog\" \"a cat and a dog\")" },
      { t: "p", text: "Use a group to limit the reach of the | . grey|gray could also be written gr(a|e)y — the group keeps the alternation local." },
      { t: "code", code: "(map #(re-find #\"gr(?:a|e)y\" %) [\"gray\" \"grey\"])" },
      { t: "h", text: "Non-capturing groups (?:…)" },
      { t: "p", text: "Above we used (?:…) instead of (…). It groups without creating a capture, so it won't clutter your result vector. Compare — only the real capture is returned:" },
      { t: "code", code: "(re-find #\"(?:Mr|Ms|Dr)\\. (\\w+)\" \"Dr. Who\")" },
      { t: "note", text: "Use (?:…) whenever you need grouping for alternation or a quantifier but don't care about capturing that part." },
    ],
    exercises: [
      {
        prompt: "Define a pattern (answer) that fully matches either \"yes\" or \"no\". It should match \"yes\" and \"no\", but not \"maybe\".",
        starter: "#\"\"",
        tests: ["(boolean (re-matches answer \"yes\"))", "(boolean (re-matches answer \"no\"))", "(nil? (re-matches answer \"maybe\"))"],
        solution: "#\"yes|no\"",
        hint: "Use alternation: #\"yes|no\".",
      },
      {
        prompt: "A group can limit where the | applies. Define a pattern (answer) that matches \"gray\" or \"grey\" using a group. It should match both spellings but not \"gravy\".",
        starter: "#\"\"",
        tests: ["(boolean (re-find answer \"gray\"))", "(boolean (re-find answer \"grey\"))", "(nil? (re-find answer \"gravy\"))"],
        solution: "#\"gr(?:a|e)y\"",
        hint: "Keep the alternation local with a (non-capturing) group: gr(?:a|e)y.",
      },
    ],
  },

  {
    id: "replace-split",
    title: "Transforming Text: replace & split",
    group: "Transforming Text",
    summary: "Rewrite and slice strings with patterns.",
    content: [
      { t: "p", text: "Matching is only half the story. clojure.string/replace (aliased here as str/replace) rewrites every match. Give it a string, a pattern, and a replacement." },
      { t: "code", code: "(str/replace \"hello world\" #\"o\" \"0\")" },
      { t: "h", text: "Referencing captured groups" },
      { t: "p", text: "In the replacement string, $1, $2, … stand for captured groups. This makes reformatting easy — here we flip a date from YYYY-MM-DD to DD/MM/YYYY." },
      { t: "code", code: "(str/replace \"2026-06-15\" #\"(\\d{4})-(\\d{2})-(\\d{2})\" \"$3/$2/$1\")" },
      { t: "h", text: "Replacing with a function" },
      { t: "p", text: "Pass a function instead of a string to compute each replacement. It receives the match (a string, or a vector when there are groups)." },
      { t: "code", code: "(str/replace \"a1 b2 c3\" #\"\\d\" (fn [d] (str \"[\" d \"]\")))" },
      { t: "h", text: "Splitting" },
      { t: "p", text: "str/split breaks a string on a pattern — great for messy separators. \\s+ splits on any run of whitespace." },
      { t: "code", code: "(str/split \"one   two\\tthree\" #\"\\s+\")" },
      { t: "note", text: "replace + capture groups ($1, $2) is one of the most useful regex skills you'll ever learn — reformatting data becomes trivial." },
    ],
    exercises: [
      {
        prompt: "Use str/replace to turn every run of whitespace in \"a   b  c\" into a single dash. Expected: \"a-b-c\".",
        starter: "(str/replace \"a   b  c\" #\"\" \"-\")",
        tests: ["(= answer \"a-b-c\")"],
        solution: "(str/replace \"a   b  c\" #\"\\s+\" \"-\")",
        hint: "Match one-or-more whitespace with #\"\\s+\" and replace with \"-\".",
      },
      {
        prompt: "Captured groups can be reused in the replacement with $1, $2. Use str/replace to flip a name from \"Last, First\" to \"First Last\": turn \"Lovelace, Ada\" into \"Ada Lovelace\".",
        starter: "(str/replace \"Lovelace, Ada\" #\"\" \"\")",
        tests: ["(= answer \"Ada Lovelace\")"],
        solution: "(str/replace \"Lovelace, Ada\" #\"(\\w+), (\\w+)\" \"$2 $1\")",
        hint: "Capture both words, then reference them in reverse: $2 then $1.",
      },
    ],
  },

  {
    id: "capstone",
    title: "Capstone: Putting It Together",
    group: "Transforming Text",
    summary: "Extract, validate and mask real-world text.",
    content: [
      { t: "p", text: "Let's combine everything on realistic tasks." },
      { t: "h", text: "Extract all the numbers" },
      { t: "p", text: "Pull every number out of a sentence and turn them into actual integers." },
      { t: "code", code: "(map js/parseInt (re-seq #\"\\d+\" \"we shipped 3 boxes, 14 books, 159 pens\"))" },
      { t: "h", text: "A simple email check" },
      { t: "p", text: "A pragmatic (not exhaustive!) email validator: some word/dot characters, an @, a domain, a dot, and a suffix — matched against the whole string." },
      { t: "code", code: "(boolean (re-matches #\"[\\w.]+@[\\w.]+\\.\\w+\" \"ada.lovelace@example.com\"))" },
      { t: "code", code: "(boolean (re-matches #\"[\\w.]+@[\\w.]+\\.\\w+\" \"not-an-email\"))" },
      { t: "h", text: "Mask sensitive digits" },
      { t: "p", text: "Replace every digit with a star to redact a phone number." },
      { t: "code", code: "(str/replace \"call me on 555-0134\" #\"\\d\" \"*\")" },
      { t: "note", text: "Real email/URL/date regexes get intricate. Start simple, test with real data, and only add complexity when a real case needs it." },
    ],
    exercises: [
      {
        prompt: "Sum every number found in \"a1 b20 c300\" (result 321). Use re-seq + js/parseInt.",
        starter: "(reduce + (map js/parseInt (re-seq #\"\" \"a1 b20 c300\")))",
        tests: ["(= answer 321)"],
        solution: "(reduce + (map js/parseInt (re-seq #\"\\d+\" \"a1 b20 c300\")))",
        hint: "Get the digit-runs with #\"\\d+\", parse each, then reduce + .",
      },
      {
        prompt: "Redact a phone number: use str/replace to replace every digit in \"call 555-0134\" with a star. Expected: \"call ***-****\".",
        starter: "(str/replace \"call 555-0134\" #\"\" \"*\")",
        tests: ["(= answer \"call ***-****\")"],
        solution: "(str/replace \"call 555-0134\" #\"\\d\" \"*\")",
        hint: "Match each digit with #\"\\d\" and replace with \"*\".",
      },
    ],
  },

  {
    id: "whats-next",
    title: "What's Next",
    group: "Wrap Up",
    summary: "Where to take your regex skills from here.",
    content: [
      { t: "p", text: "Nicely done! You can now match, extract, validate and transform text with regular expressions — a skill that pays off in every programming language, editor and command-line tool you'll ever use." },
      { t: "h", text: "Ideas to keep practising" },
      { t: "list", items: [
        "Parse log lines into fields with capture groups.",
        "Clean up messy CSV/whitespace with split and replace.",
        "Write a validator for phone numbers or dates in your locale.",
        "Reformat names ('Last, First' → 'First Last') using $1/$2.",
      ] },
      { t: "h", text: "Good to know" },
      { t: "list", items: [
        "Flags: many tools add flags like case-insensitive (i), multiline (m) and dot-matches-newline (s). Syntax varies by language.",
        "Dialects differ slightly (this course used the JavaScript flavour via ClojureScript). Core ideas transfer everywhere.",
        "Tools like regex101.com are great for experimenting and explaining patterns.",
      ] },
      { t: "note", text: "Keep the REPL and Cheat Sheet handy, and reach for regex whenever you catch yourself doing fiddly string surgery by hand. Happy matching!" },
      { t: "cta", text: "This is the end of the road — you've worked through Clojure, ClojureScript and Regex. Fancy a refresher, or want to finish any lessons you skipped? Jump back to Course 1 any time.", label: "Back to the start: Learn Clojure", courseId: "clojure" },
    ],
    exercises: [],
  },
];
